/**
 * What a business can switch on, as a closed vocabulary.
 *
 * THE LIBRO IS THE KERNEL; A MODULE IS A BUNDLE OF SCREENS AND A SOURCE OF
 * LEDGER ENTRIES. `cuotas` is the first: Cobros, Clientes, Planes, the
 * WhatsApp reminder. `pedidos` is named here before any screen reads it, so
 * the vocabulary, the templates and the database `check` change once.
 *
 * WHAT A MODULE CONTRIBUTES TO EACH SURFACE DOES NOT LIVE HERE. The web's
 * navigation, the phone's tab bar and the sample scenarios are each already
 * a table of data with its own test; a central table describing all of them
 * would have to know React Router paths, expo-router file names and React
 * components. Each registry keeps its shape and is tagged with a ModuleId.
 *
 * Three rules, from the design spec (§1.1):
 *   1. A module depends on the kernel and on nothing else -- never on another
 *      module.
 *   2. The owner almost never sees the switches; the business type sets them.
 *      The word «módulo» never reaches a screen: `label` and `hint` are what
 *      the owner reads.
 *   3. Switching one off hides; it deletes nothing and stops nothing.
 */
// New ids go at the END: stored ids never move.
export const MODULE_IDS = ["cuotas", "pedidos", "inventario", "planilla", "citas", "facturacion", "bancos"];
export const MODULES = {
    cuotas: {
        id: "cuotas",
        label: "Cobra mensualidades o cuotas",
        // IT DOES NOT PROMISE CLIENTES: that screen is the kernel's -- a bakery
        // and a workshop have customers and no plans at all -- and switching
        // cuotas off does not take the customer list with it.
        hint: "Para cobrar mensualidades o cuotas sin perder la cuenta de quién debe.",
        adds: ["Cobros: quién pagó y quién debe este mes.", "Planes: cuánto se cobra y cada cuánto.", "El recordatorio de pago por WhatsApp."],
        needs: null,
        decidedOnly: false,
        addOn: false,
    },
    pedidos: {
        id: "pedidos",
        label: "Toma pedidos por encargo",
        hint: "Para los trabajos por encargo que se entregan otro día.",
        adds: ["Pedidos: para quién, qué y para cuándo.", "Cuánto te adelantaron y cuánto falta por cobrar."],
        needs: null,
        decidedOnly: false,
        addOn: false,
    },
    inventario: {
        id: "inventario",
        // A NAME, NOT A QUESTION: an add-on is a switch in Configuración → Add-ons
        // never a Sí/No asked like the wizard's modules.
        label: "Inventario",
        hint: "Para saber cuánto te queda de cada producto y cuándo comprar.",
        adds: [
            "Cada producto dice cuántos tienes.",
            "Anotas lo que entra y lo que sale.",
            "Cuentas lo que hay y corriges las diferencias.",
        ],
        needs: null,
        decidedOnly: false,
        addOn: true,
    },
    planilla: {
        id: "planilla",
        // A NAME, like inventario: an add-on is a switch, never a Sí/No question.
        label: "Planilla",
        hint: "Para pagarle a tu gente sin sacar las cuentas a mano.",
        adds: [
            "Calcula los descuentos y aportes de ley.",
            "El talonario de cada persona.",
            "El reporte del mes y el del año, para tu contador.",
        ],
        // No cédula: a person is paid without one.
        needs: "Tu gente anotada en Empleados, con lo que gana.",
        decidedOnly: false,
        addOn: true,
    },
    citas: {
        id: "citas",
        label: "Agenda citas",
        hint: "Para dar citas sin cruzar horarios.",
        adds: ["La Agenda del día y de la semana.", "Cada cita con su profesional y su hora."],
        needs: null,
        // Decided: a bakery is not asked whether it books appointments.
        decidedOnly: true,
        addOn: false,
    },
    facturacion: {
        id: "facturacion",
        // A NAME, like inventario and planilla: an add-on is a switch, never a question.
        label: "Facturación electrónica",
        hint: "Para entregar facturas válidas ante la DGI.",
        adds: ["Desde una venta cobrada emites su factura.", "Notas de crédito para corregirla."],
        needs: "Un contrato con un PAC, el proveedor que autoriza la DGI. Al encenderla te pedimos sus datos.",
        decidedOnly: false,
        // Offered by no family or vertical: the server offers it to the businesses
        // in its list (spec 2026-10-02 §3), through `switchableModuleIds`' `offer`.
        addOn: true,
    },
    bancos: {
        id: "bancos",
        // «BANCOS Y CAJA», NOT «CUENTAS» (spec §4.0): the
        // restaurant already calls its tables' bills «Cuentas». Offered to every
        // family in every plan, born on only in the church. Off, nothing about the
        // accounts shows, and every movement still lands in its account underneath.
        label: "Bancos y caja",
        hint: "Para saber cuánto dinero tienes y dónde está.",
        adds: [
            "Cada cobro y cada gasto dice en qué cuenta entró o de cuál salió.",
            "El saldo de cada banco y de la caja.",
            "Lo que pasas de una a otra, como un depósito.",
        ],
        needs: null,
        decidedOnly: false,
        addOn: true,
    },
};
export function hasModule(modules, id) {
    return modules.includes(id);
}
/**
 * The modules the wizard used to ask about -- every one that is neither
 * `decidedOnly` nor an add-on. Nobody is asked any more, but a business that
 * answered keeps them, so `isWithdrawnModule` still lists one that is on.
 */
export function askableModuleIds() {
    return MODULE_IDS.filter((id) => !MODULES[id].decidedOnly && !MODULES[id].addOn);
}
